import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Database, Search, RefreshCw, Globe, 
  ArrowUpDown, Languages, Calendar, Percent
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Import centralized language utilities
import { getCountryCodeFromString } from '../utils/languageUtils';

const getCountryCode = getCountryCodeFromString;

const FlagImage = ({ countryCode, className = "w-6 h-4" }) => {
  if (!countryCode) {
    return <Globe className={`${className} text-muted-foreground`} />;
  }
  return (
    <img 
      src={`https://flagcdn.com/w40/${countryCode}.png`}
      alt={countryCode}
      className={`${className} object-cover rounded`}
      loading="lazy"
    />
  );
};

// Color coding for correlation percentage
const getCorrelationBadge = (correlation) => {
  if (correlation >= 80) {
    return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">{correlation}%</Badge>;
  } else if (correlation >= 50) {
    return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">{correlation}%</Badge>;
  } else if (correlation >= 20) {
    return <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">{correlation}%</Badge>;
  } else {
    return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">{correlation}%</Badge>;
  }
};

export default function AdminFrequencies() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [languages, setLanguages] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('word_count');
  const [sortDirection, setSortDirection] = useState('desc');

  // Check admin access
  useEffect(() => {
    if (user && !user.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Fetch frequency languages
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/frequencies`,
        { withCredentials: true }
      );
      setLanguages(response.data.languages || []);
      setStats({
        total_languages: response.data.total_languages,
        total_words: response.data.total_words,
        english_word_count: response.data.english_word_count
      });
    } catch (err) {
      console.error('Error fetching frequencies:', err);
      toast.error('Failed to load frequency data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) {
      fetchData();
    }
  }, [user]);

  // Filter and sort languages
  const filteredLanguages = languages
    .filter(lang => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        lang.language_code.toLowerCase().includes(query) ||
        lang.language_name.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortButton = ({ field, label }) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => handleSort(field)}
      className="h-8 px-2 text-xs font-medium hover:bg-white/5"
    >
      {label}
      <ArrowUpDown className={`ml-1 h-3 w-3 ${sortField === field ? 'text-primary' : 'text-muted-foreground'}`} />
    </Button>
  );

  if (!user?.is_admin) {
    return null;
  }

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="admin-frequencies-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="w-6 h-6 text-amber-400" />
            Vocabulary Statistics
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Word frequency data and translation overlap analysis
          </p>
        </div>
        <Button 
          onClick={fetchData} 
          disabled={loading}
          variant="outline"
          size="sm"
          data-testid="refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Languages className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_languages}</p>
                  <p className="text-xs text-muted-foreground">Languages</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <Database className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{(stats.total_words / 1000000).toFixed(2)}M</p>
                  <p className="text-xs text-muted-foreground">Total Words</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <Percent className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.english_word_count?.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">English Reference</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <Card className="bg-card/50 border-border/50">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by language name or code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-background/50"
              data-testid="search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Languages Table */}
      <Card className="bg-card/50 border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Languages ({filteredLanguages.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredLanguages.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No languages found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50 hover:bg-transparent">
                    <TableHead className="w-12"></TableHead>
                    <TableHead>
                      <SortButton field="language_name" label="Language" />
                    </TableHead>
                    <TableHead className="text-center">Code</TableHead>
                    <TableHead className="text-right">
                      <SortButton field="word_count" label="Words" />
                    </TableHead>
                    <TableHead className="text-center">
                      <SortButton field="english_correlation" label="Translate Rate" />
                    </TableHead>
                    <TableHead className="text-center">
                      <SortButton field="last_updated" label="Last Updated" />
                    </TableHead>
                    <TableHead className="text-center">Source</TableHead>
                    <TableHead className="text-right">Enriched</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLanguages.map((lang) => (
                    <TableRow 
                      key={lang.language_code}
                      className="border-border/30 hover:bg-white/[0.02]"
                      data-testid={`lang-row-${lang.language_code}`}
                    >
                      <TableCell className="w-12">
                        <FlagImage countryCode={getCountryCode(lang.language_code)} />
                      </TableCell>
                      <TableCell className="font-medium">
                        {lang.language_name}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="font-mono text-xs">
                          {lang.language_code}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {lang.word_count.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-center">
                        {getCorrelationBadge(lang.english_correlation)}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1 text-muted-foreground text-sm">
                          <Calendar className="w-3 h-3" />
                          {lang.last_updated}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary" className="text-xs">
                          {lang.source === 'opensubtitles_2018' ? 'OpenSubs' : lang.source === 'opensubtitles' ? 'OpenSubs' : lang.source}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        <span className={lang.enriched_count > 0 ? 'text-emerald-400' : 'text-muted-foreground'}>
                          {(lang.enriched_count || 0).toLocaleString()}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <Card className="bg-card/30 border-border/30">
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground mb-2 font-medium">Translate Rate Legend (% of words with direct English equivalents):</p>
          <div className="flex flex-wrap gap-3 text-xs">
            <div className="flex items-center gap-1.5">
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">80%+</Badge>
              <span className="text-muted-foreground">High overlap (Latin-based)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">50-79%</Badge>
              <span className="text-muted-foreground">Medium overlap</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">20-49%</Badge>
              <span className="text-muted-foreground">Low overlap</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30">&lt;20%</Badge>
              <span className="text-muted-foreground">Different script/family</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
